import React, { useState } from 'react';
import { Phone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [learnForm, setLearnForm] = useState({
    name: '',
    email: '',
    phone: '',
    interest: ''
  });
  
  const [helpForm, setHelpForm] = useState({
    name: '',
    email: '',
    phone: '',
    skills: ''
  });

  const [isSubmitting, setIsSubmitting] = useState({ learn: false, help: false });

  const handleLearnSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting({ ...isSubmitting, learn: true });

    try {
      const response = await axios.post(`${API}/contact/learn`, learnForm);
      toast.success(response.data.message);
      setLearnForm({ name: '', email: '', phone: '', interest: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting({ ...isSubmitting, learn: false });
    }
  };

  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting({ ...isSubmitting, help: true });

    try {
      const response = await axios.post(`${API}/contact/help`, helpForm);
      toast.success(response.data.message);
      setHelpForm({ name: '', email: '', phone: '', skills: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting({ ...isSubmitting, help: false });
    }
  };

  return (
    <div>
      {/* Contact Info Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Let's Connect
            </h2>
            <div className="section-underline"></div>
            <p className="text-xl text-gray-600">
              Questions? We're here to help you take the first step
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Call Us */}
            <div className="feature-card">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mb-4 shadow-lg">
                <Phone className="text-white" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Call Us</h3>
              <a 
                href="tel:8003185922" 
                className="text-2xl font-bold text-cyan-500 hover:text-cyan-600 mb-2 block"
              >
                (800) 318-5922
              </a>
              <p className="text-gray-600">Mon-Fri, 9am-5pm EST</p>
            </div>

            {/* Email Us */}
            <div className="feature-card">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center mb-4 shadow-lg">
                <Mail className="text-white" size={24} />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Email Us</h3>
              <a 
                href="mailto:info@the317foundation.org" 
                className="text-2xl font-bold text-purple-500 hover:text-purple-600 mb-2 block break-all"
              >
                info@the317foundation.org
              </a>
              <p className="text-gray-600">24-hour response time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section id="contact" className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Ready to Level Up?
            </h2>
            <div className="section-underline"></div>
            <p className="text-xl text-gray-600">
              Choose your path and let's get started today
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* I Want to Learn Form */}
            <div className="feature-card">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">I Want to Learn</h3>
              <p className="text-gray-600 mb-6">Start your tech career journey now</p>
              
              <form onSubmit={handleLearnSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="learn-name">Full Name *</Label>
                  <Input
                    id="learn-name"
                    type="text"
                    required
                    value={learnForm.name}
                    onChange={(e) => setLearnForm({ ...learnForm, name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="learn-email">Email *</Label>
                  <Input
                    id="learn-email"
                    type="email"
                    required
                    value={learnForm.email}
                    onChange={(e) => setLearnForm({ ...learnForm, email: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="learn-phone">Phone Number *</Label>
                  <Input
                    id="learn-phone"
                    type="tel"
                    required
                    value={learnForm.phone}
                    onChange={(e) => setLearnForm({ ...learnForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="learn-interest">What interests you?</Label>
                  <Textarea
                    id="learn-interest"
                    value={learnForm.interest}
                    onChange={(e) => setLearnForm({ ...learnForm, interest: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting.learn}
                >
                  {isSubmitting.learn ? 'Sending...' : 'Apply Now'}
                </button>
              </form>
            </div>

            {/* I Want to Help Form */}
            <div className="feature-card">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">I Want to Help</h3>
              <p className="text-gray-600 mb-6">Make a difference by volunteering</p>
              
              <form onSubmit={handleHelpSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="help-name">Full Name *</Label>
                  <Input
                    id="help-name"
                    type="text"
                    required
                    value={helpForm.name}
                    onChange={(e) => setHelpForm({ ...helpForm, name: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="help-email">Email *</Label>
                  <Input
                    id="help-email"
                    type="email"
                    required
                    value={helpForm.email}
                    onChange={(e) => setHelpForm({ ...helpForm, email: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="help-phone">Phone Number *</Label>
                  <Input
                    id="help-phone"
                    type="tel"
                    required
                    value={helpForm.phone}
                    onChange={(e) => setHelpForm({ ...helpForm, phone: e.target.value })}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="help-skills">Your Skills & Experience</Label>
                  <Textarea
                    id="help-skills"
                    value={helpForm.skills}
                    onChange={(e) => setHelpForm({ ...helpForm, skills: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting.help}
                >
                  {isSubmitting.help ? 'Sending...' : 'Join the Team'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;